import * as assetsClient from '@/services/assets-api/assetsClient';
import { AssetsResponse, AssetsResponseBulk, ImageVersions } from '@/services/assets-api/assetsClient';
import { toasted } from '@/ui/helpers';
import { t } from '@/ui/i18n/i18n';
import { base64ToFile } from '@/services/file/fileProcessor';
import { v4 as uuidv4 } from 'uuid';
import { blobToDataURL } from '@/_legacy/util/blob';
import PhotoReader from '@/_legacy/util/PhotoReader';
import { validateFile } from '@/services/assets-api/assetsValidation';

const toastUploadFailure = () => toasted.error(t('assets.upload-failure'), { singleton: true });
const toastRemoveFailure = () => toasted.error(t('assets.remove-failure'), { singleton: true });

interface ResizedPhoto {
  photo: string;
  thumbnail: string;
  width: number;
  height: number;
}

interface AssetsResizedResponse extends AssetsResponse, ResizedPhoto {}

export const uploadImage = async ({
  file,
  namespace,
  subnamespace,
  fileId,
}: {
  file: File;
  namespace: string;
  subnamespace: string;
  fileId: string | undefined;
}): Promise<AssetsResizedResponse | boolean> => {
  try {
    if (!validateFile(file)) {
      return false;
    }

    const resizedPhoto: ResizedPhoto = await PhotoReader.read(file);

    const binaryFile = base64ToFile(resizedPhoto.photo, uuidv4());

    const uploadResponse = await assetsClient.uploadImage({
      file: binaryFile,
      namespace,
      subnamespace,
      fileId,
    });

    if (!uploadResponse) {
      toastUploadFailure();
      return false;
    }

    return { ...(uploadResponse as AssetsResizedResponse), ...resizedPhoto };
  } catch (e) {
    console.error(e);
    toastUploadFailure();
  }

  return false;
};

// assume already resized, use for drawings.
// if drawing resized and converted back - it has black background
export const uploadBase64Image = async ({
  file,
  namespace,
  subnamespace,
  fileId,
}: {
  file: string;
  namespace: string;
  subnamespace: string;
  fileId?: string;
}): Promise<AssetsResponse | boolean> => {
  try {
    const binaryFile = base64ToFile(file, uuidv4());

    if (!validateFile(binaryFile)) {
      return false;
    }

    const uploadResponse = await assetsClient.uploadImage({
      file: binaryFile,
      namespace,
      subnamespace,
      fileId,
    });

    if (!uploadResponse) {
      toastUploadFailure();
      return false;
    }

    return uploadResponse;
  } catch (e) {
    console.error(e);
    toastUploadFailure();
  }

  return false;
};

interface FileEntry {
  name: string;
  file: string;
}

export const uploadBase64ImageMany = async ({
  files,
  namespace,
  subnamespace,
}: {
  files: Array<FileEntry>;
  namespace: string;
  subnamespace: string;
}): Promise<AssetsResponseBulk | boolean> => {
  try {
    const binaryFiles = files.map(
      (fileEntry: FileEntry) => base64ToFile(fileEntry.file, fileEntry.name || uuidv4()),
    );

    // eslint-disable-next-line no-restricted-syntax
    for (const binaryFile of binaryFiles) {
      if (!validateFile(binaryFile)) {
        return false;
      }
    }

    const uploadResponse = await assetsClient.uploadImageMany({
      files: binaryFiles,
      namespace,
      subnamespace,
    });

    if (!uploadResponse) {
      toastUploadFailure();
      return false;
    }

    return uploadResponse;
  } catch (e) {
    console.error(e);
    toastUploadFailure();
  }

  return false;
};

export const removeImage = async (
  fileId: string,
): Promise<boolean> => {
  try {
    const response = await assetsClient.removeImage(fileId);

    if (!response) {
      toastRemoveFailure();
      return false;
    }

    return true;
  } catch (e) {
    console.error(e);
    toastRemoveFailure();
  }

  return false;
};

export const removeImageBulk = async (
  fileIds: Array<string | null | undefined>,
): Promise<boolean> => {
  try {
    const notEmptyIds = fileIds.filter(fileId => !!fileId) as Array<string>;
    const response = await assetsClient.removeImageMany(notEmptyIds);

    if (!response) {
      toastRemoveFailure();
      return false;
    }

    return true;
  } catch (e) {
    console.error(e);
    toastRemoveFailure();
  }

  return false;
};

export const getImageAsDataUrl = async (
  fileId: string,
  version?: ImageVersions,
): Promise<string | null> => {
  const imageBlob = await assetsClient.getImage(fileId, version);
  if (!imageBlob) {
    return null;
  }

  return blobToDataURL(imageBlob);
};

export const uploadDocument = async ({
  file,
  namespace,
  subnamespace,
  fileId,
}: {
  file: File;
  namespace: string;
  subnamespace: string;
  fileId?: string | undefined;
}, validate = true): Promise<AssetsResponse | boolean> => {
  try {
    if (validate && !validateFile(file, 'file')) {
      return false;
    }

    const uploadResponse = await assetsClient.uploadDocument({
      file,
      namespace,
      subnamespace,
      fileId,
    });

    if (!uploadResponse) {
      toastUploadFailure();
      return false;
    }

    return uploadResponse;
  } catch (e) {
    console.error(e);
    toastUploadFailure();
  }

  return false;
};

export const removeDocument = async (
  fileId: string,
): Promise<boolean> => {
  try {
    const response = await assetsClient.removeDocument(fileId);

    if (!response) {
      toastRemoveFailure();
      return false;
    }

    return true;
  } catch (e) {
    console.error(e);
    toastRemoveFailure();
  }

  return false;
};
